import mergeRefs from 'merge-refs';
import { forwardRef, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import Modal from '@/components/common/modal/modal';
import type { ModalAction } from '@/components/common/modal/modal.type';
import FormInput from '../common/form-input';

interface Props {
  type: 'create' | 'edit';
  onSubmit: (tagName: string) => Promise<void>;
  defaultValue?: string;
}

const TagNameModal = forwardRef<ModalAction, Props>(({ type, onSubmit, defaultValue }, ref) => {
  const { t } = useTranslation();
  const modalRef = useRef<ModalAction>(null);
  const {
    handleSubmit,
    formState: { isSubmitting },
    control,
    watch,
  } = useForm<{ tagName: string }>({
    defaultValues: { tagName: defaultValue },
  });

  const tagName = watch('tagName');

  const handleCreateTag = (data: { tagName: string }) => onSubmit(data.tagName);

  return (
    <Modal
      ref={mergeRefs(modalRef, ref)}
      contentClassName='w-fit rounded-none p-6 rounded-2xl'>
      <div className='text-base font-bold mb-4'>
        {type === 'edit' ? t('contact.tags.change-name') : t('contact.tags.add-tag')}
      </div>

      <form
        className='flex flex-col gap-4'
        onSubmit={handleSubmit(handleCreateTag)}>
        <FormInput
          control={control}
          maxLength={12}
          name='tagName'
          showClearButton
          required
          placeholder={t('contact.tags.create.placeholder')}
        />
        <div className='flex justify-end gap-2'>
          <button
            type='button'
            className='btn h-10 min-h-10 font-normal btn-ghost order-first'
            onClick={() => {
              modalRef.current?.close();
            }}>
            {t('common.cancel')}
          </button>

          <button
            className='btn h-10 min-h-10 font-normal btn-primary '
            type='submit'
            disabled={!tagName || isSubmitting}>
            {isSubmitting ? <span className='loading loading-spinner loading-sm' /> : t('common.confirm')}
          </button>
        </div>
      </form>
    </Modal>
  );
});

TagNameModal.displayName = 'TagNameModal';

export default TagNameModal;

const tagNameFormSchema = z.object({});
